import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import config from "../../config";
import { defaultCustomRange, isCustomRangeValid, todayInputValue } from "../../utils/dateInput";
import { formatDisplayDate } from '../../utils/formatDate';

const parseSelectedFeatures = (value) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "-";
  const datePart = formatDisplayDate(d);
  const timePart = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
  return `${datePart}, ${timePart}`;
};

const getStatusBadge = (status) => {
  if (status === "pending") {
    return <span className="hs-badge hs-badge-warning">Pending</span>;
  }
  if (status === "accepted") {
    return <span className="hs-badge hs-badge-success">Accepted</span>;
  }
  if (status === "rejected") {
    return <span className="hs-badge hs-badge-danger">Rejected</span>;
  }
  return <span className="hs-badge hs-badge-neutral">{status || "-"}</span>;
};

const ReservationsTable = () => {
  const { auth, user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [venuesList, setVenuesList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedVenue, setSelectedVenue] = useState("all");
  const [selectedPeriod, setSelectedPeriod] = useState("30d");
  const [customRange, setCustomRange] = useState(defaultCustomRange);
  const [filterStatus, setFilterStatus] = useState("all");

  // Detail Modal State
  const [activeReservation, setActiveReservation] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const isAdminView = useMemo(() => {
    if (!user) return false;
    const perms = Array.isArray(user.permissions) ? user.permissions : [];
    return user.role === "admin" || perms.includes("admin_access");
  }, [user]);

  // Fetch Venues List for dropdown filter
  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const res = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues?limit=150`);
        if (res.ok) {
          const data = await res.json();
          setVenuesList(data.data || []);
        }
      } catch (e) {
        console.error("Error fetching venues list for filter:", e);
      }
    };
    if (isAdminView) {
      fetchVenues();
    }
  }, [auth, isAdminView]);

  const fetchReservations = useCallback(async () => {
    if (selectedPeriod === "custom" && !isCustomRangeValid(customRange)) return;

    setLoading(true);
    try {
      const basePath = isAdminView ? "/api/reservations" : "/api/reservations/my";
      const url = new URL(`${config.API_BASE_URL}${basePath}`);
      if (filterStatus !== "all") url.searchParams.set("status", filterStatus);
      if (selectedVenue !== "all") url.searchParams.set("venue_id", selectedVenue);
      if (selectedPeriod !== "all") url.searchParams.set("period", selectedPeriod);
      if (selectedPeriod === "custom") {
        url.searchParams.set("from", customRange.from);
        url.searchParams.set("to", customRange.to);
      }

      const response = await auth.fetch(url.toString());
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to fetch reservations");
      }
      const data = await response.json();
      setReservations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching reservations:", err);
      toast.error("Failed to load reservations");
    } finally {
      setLoading(false);
    }
  }, [auth, filterStatus, selectedVenue, selectedPeriod, customRange, isAdminView]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const updateReservationStatus = async (reservation, status) => {
    if (status === "rejected" && !window.confirm("Reject this reservation? The customer will be notified.")) return;
    setUpdatingStatus(true);
    try {
      const response = await auth.fetch(`${config.API_BASE_URL}/api/reservations/${reservation.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) throw new Error("Failed to update reservation");
      toast.success(status === "accepted" ? "Reservation accepted" : "Reservation rejected");
      setActiveReservation(null);
      fetchReservations();
    } catch (err) {
      console.error("Error updating reservation:", err);
      toast.error("Could not update the reservation. Please try again.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12 border-0 shadow-sm">
      <div className="card-header border-bottom bg-white py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        {/* Left: Filters */}
        <div className="d-flex align-items-center flex-wrap gap-3">
          {isAdminView && (
            <div className="d-flex align-items-center gap-2">
              <span className="text-xs fw-semibold text-secondary-light">Venue:</span>
              <select
                className="hs-select text-xs"
                style={{ minWidth: '180px' }}
                value={selectedVenue}
                onChange={(e) => setSelectedVenue(e.target.value)}
              >
                <option value="all">All Venues</option>
                {venuesList.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.title || v.business_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="d-flex align-items-center gap-2">
            <span className="text-xs fw-semibold text-secondary-light">Period:</span>
            <select
              className="hs-select text-xs"
              style={{ minWidth: '140px' }}
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
            >
              <option value="7d">7 Days</option>
              <option value="30d">30 Days</option>
              <option value="3m">3 Months</option>
              <option value="6m">6 Months</option>
              <option value="12m">12 Months</option>
              <option value="all">All Time</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {selectedPeriod === "custom" && (
            <div className="d-flex align-items-center gap-2">
              <input
                type="date"
                aria-label="From date"
                className="form-control form-control-sm radius-8"
                value={customRange.from}
                max={customRange.to || todayInputValue()}
                onChange={(e) => setCustomRange((range) => ({ ...range, from: e.target.value }))}
              />
              <span className="text-xs fw-semibold text-secondary-light">to</span>
              <input
                type="date"
                aria-label="To date"
                className="form-control form-control-sm radius-8"
                value={customRange.to}
                min={customRange.from}
                max={todayInputValue()}
                onChange={(e) => setCustomRange((range) => ({ ...range, to: e.target.value }))}
              />
            </div>
          )}

          <div className="d-flex align-items-center gap-2">
            <span className="text-xs fw-semibold text-secondary-light">Status:</span>
            <select
              className="hs-select text-xs"
              style={{ minWidth: '130px' }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Right: Counter & Refresh */}
        <div className="d-flex align-items-center gap-3">
          <span className="text-sm fw-medium text-secondary-light">
            {loading ? "Loading..." : reservations.length > 0 ? `${reservations.length} reservations found` : "No reservations found for the selected filters"}
          </span>
          <button
            type="button"
            className="hs-btn-secondary hs-btn-sm d-inline-flex align-items-center gap-2"
            onClick={fetchReservations}
          >
            <Icon icon="solar:refresh-bold" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="hs-table mb-0">
            <thead>
              <tr>
                <th scope="col" style={{ width: '110px' }}>Status</th>
                <th scope="col">Date</th>
                <th scope="col">Venue</th>
                <th scope="col">Customer</th>
                <th scope="col">Selected Offers</th>
                <th scope="col">Message</th>
                <th scope="col" className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : reservations.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-secondary-light">
                    <Icon
                      icon="solar:calendar-broken"
                      className="text-5xl text-secondary-light mb-2 opacity-50"
                    />
                    <p className="text-secondary-light mb-0">No reservations found for the selected filters</p>
                  </td>
                </tr>
              ) : (
                reservations.map((r) => {
                  const features = parseSelectedFeatures(r.selected_features);
                  const featuresLabel =
                    features.length === 0
                      ? "-"
                      : features
                          .slice(0, 3)
                          .map((f) => f?.name || f?.title || `#${f?.id || ""}`)
                          .filter(Boolean)
                          .join(", ");

                  return (
                    <tr key={r.id}>
                      <td>{getStatusBadge(r.status)}</td>
                      <td className="text-nowrap text-xs text-secondary-light">{formatDate(r.created_at)}</td>
                      <td>
                        <span className="fw-semibold text-dark text-sm">{r.venue_name || "-"}</span>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <span className="fw-medium text-dark text-sm">
                            {r.customer_name || "-"}
                          </span>
                          <span className="text-xs text-secondary-light">
                            {r.customer_email || r.customer_phone || "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="text-secondary-light text-sm">{featuresLabel}</span>
                        {features.length > 3 && (
                          <span className="text-secondary-light text-xs"> +{features.length - 3}</span>
                        )}
                      </td>
                      <td>
                        {r.customer_message ? (
                          <div 
                            className="text-truncate text-secondary-dark text-xs" 
                            style={{ maxWidth: '200px' }}
                            title={r.customer_message}
                          >
                            {r.customer_message}
                          </div>
                        ) : (
                          <span className="text-secondary-light text-xs">-</span>
                        )}
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary px-3 py-1 radius-8 fw-semibold d-inline-flex align-items-center gap-1 text-xs"
                          onClick={() => setActiveReservation(r)}
                        >
                          <Icon icon="solar:eye-bold" />
                          <span>View Details</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reservation Details Modal */}
      {activeReservation && (
        <Modal show={Boolean(activeReservation)} onHide={() => setActiveReservation(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold text-dark">
              Reservation Details #{activeReservation.id}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex flex-column gap-3">
              <div className="d-flex align-items-center justify-content-between p-3 bg-light radius-8">
                <div>
                  <span className="text-xs text-secondary-light d-block">Status</span>
                  <div className="mt-1">{getStatusBadge(activeReservation.status)}</div>
                </div>
                <div className="text-end">
                  <span className="text-xs text-secondary-light d-block">Submission Date</span>
                  <span className="text-sm fw-medium text-dark">{formatDate(activeReservation.created_at)}</span>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-6">
                  <span className="text-xs text-secondary-light d-block">Venue</span>
                  <span className="text-sm fw-semibold text-dark">{activeReservation.venue_name || "-"}</span>
                </div>
                <div className="col-6">
                  <span className="text-xs text-secondary-light d-block">Reservation Date / Time</span>
                  <span className="text-sm fw-semibold text-dark">{formatDate(activeReservation.reservation_date || activeReservation.booking_date || activeReservation.created_at)}</span>
                </div>
                <div className="col-6">
                  <span className="text-xs text-secondary-light d-block">Customer Name</span>
                  <span className="text-sm fw-medium text-dark">{activeReservation.customer_name || "-"}</span>
                </div>
                <div className="col-6">
                  <span className="text-xs text-secondary-light d-block">Customer Email</span>
                  <span className="text-sm fw-medium text-dark">{activeReservation.customer_email || "-"}</span>
                </div>
                {activeReservation.customer_phone && (
                  <div className="col-6">
                    <span className="text-xs text-secondary-light d-block">Phone Number</span>
                    <span className="text-sm fw-medium text-dark">{activeReservation.customer_phone}</span>
                  </div>
                )}
                {activeReservation.party_size && (
                  <div className="col-6">
                    <span className="text-xs text-secondary-light d-block">Guests / Party Size</span>
                    <span className="text-sm fw-medium text-dark">{activeReservation.party_size} people</span>
                  </div>
                )}
              </div>

              {parseSelectedFeatures(activeReservation.selected_features).length > 0 && (
                <div>
                  <span className="text-xs text-secondary-light d-block mb-1">Selected Features &amp; Offers</span>
                  <div className="d-flex flex-wrap gap-1">
                    {parseSelectedFeatures(activeReservation.selected_features).map((f, idx) => (
                      <span key={idx} className="badge bg-primary-50 text-primary-600 border border-primary-100 px-2 py-1 radius-4 text-xs">
                        {f.name || f.title || `#${f.id || idx}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {activeReservation.customer_message && (
                <div>
                  <span className="text-xs text-secondary-light d-block mb-1">Customer Message</span>
                  <div className="p-3 bg-light radius-8 text-sm text-secondary-dark" style={{ whiteSpace: 'pre-wrap' }}>
                    {activeReservation.customer_message}
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary px-3 py-1 radius-8 fw-semibold"
              onClick={() => setActiveReservation(null)}
            >
              Close
            </button>
            {!isAdminView && activeReservation.status === "pending" && (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger px-3 py-1 radius-8 fw-semibold"
                  disabled={updatingStatus}
                  onClick={() => updateReservationStatus(activeReservation, "rejected")}
                >
                  Reject
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary px-3 py-1 radius-8 fw-semibold"
                  disabled={updatingStatus}
                  onClick={() => updateReservationStatus(activeReservation, "accepted")}
                >
                  Accept
                </button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default ReservationsTable;
